import React, { useState } from 'react';
import {
  IconButton,
  IconSettingsLine,
  Menu,
  Text,
  Modal,
  Button,
  Flex,
  Link,
  IamProtected,
  useIamValidation
} from '@smarterservices/smarter-ui';
import { useHotkeys } from 'react-hotkeys-hook';
import { DraggableModal } from '@ui/shared/components/Common/DraggableModal';
import CONSTANTS from '../../../config/constants';
import Shortcuts from './Shortcuts';
import NavigationalMenuItem from '@ui/shared/components/Common/NavigationalMenuItem';
import IncidentReport from '../../SessionDetails/IncidentReport';
import SessionLog from '../../SessionDetails/SessionLog';
import { getStore, useExamSessionStore } from '../../../stores/StoreContext';
import { useFullScreenMountNode } from '../../../contexts/FullScreenContext';

const SessionHelp = () => {
  const [isShortcutsModalOpen, setIsShortcutsModalOpen] = useState(false);
  const [isIncidentReportModalOpen, setIsIncidentReportModalOpen] = useState(false);
  const [isSessionLogModalOpen, setIsSessionLogModalOpen] = useState(false);
  const [isDraftWarningModalOpen, setIsDraftWarningModalOpen] = useState(false);

  const ExamSessionStore = getStore('examSessionStore');
  const AppStore = getStore('appStore');
  const { checkPermission, checkPermissionsOr } = useIamValidation();

  const reviewRequired = useExamSessionStore((state) => state.sessionData?.reviewRequired);
  const reviewCompletedDate = useExamSessionStore((state) => state.sessionData?.reviewCompletedDate);
  const incidentReport = useExamSessionStore((state) => state.incidentReport);

  // Get the appropriate mount node for modals (full screen container or document.body)
  const mountNode = useFullScreenMountNode();


  const initialSize = { width: 500, height: 500 };

  const handleOpenShortcutsModal = () => {
    setIsShortcutsModalOpen(true);
  };

  const handleCloseShortcutsModal = () => {
    setIsShortcutsModalOpen(false);
  };

  const handleOpenIncidentReportModal = () => {
    setIsIncidentReportModalOpen(true);
  };

  const handleCloseIncidentReportModal = () => {
    setIsIncidentReportModalOpen(false);
  };

  const handleSetAsAudited = async () => {
    // Check if there's a draft incident report
    if (incidentReport?.draft) {
      setIsDraftWarningModalOpen(true);
      return;
    }

    // Proceed with setting as audited if no draft report
    await ExamSessionStore.setSessionAsReviewed({ reviewByUser: AppStore.user.username });
  };

  const handleCloseDraftWarningModal = () => {
    setIsDraftWarningModalOpen(false);
  };

  // Register keyboard shortcuts using react-hotkeys-hook
  useHotkeys(CONSTANTS?.KEYBOARD_SHORTCUTS?.VIEW?.OPTIONS?.HELP?.KEY, handleOpenShortcutsModal, {
    enabled: !isShortcutsModalOpen,
    preventDefault: true
  });
  useHotkeys(CONSTANTS?.KEYBOARD_SHORTCUTS?.INCIDENT_REPORT?.OPTIONS?.CREATE?.KEY, handleOpenIncidentReportModal, {
    enabled: !isIncidentReportModalOpen,
    preventDefault: true
  });
  useHotkeys(CONSTANTS?.KEYBOARD_SHORTCUTS?.VIEW?.OPTIONS?.SESSION_LOG?.KEY, () => setIsSessionLogModalOpen(true), {
    enabled: !isSessionLogModalOpen,
    preventDefault: true
  });


  return (
    <>
      <Menu
        trigger={
          <IconButton
            as="span"
            size="small"
            style={{ zIndex: '99999' }}
            screenReaderLabel="Session Options"
            withBackground={false}
            withBorder={false}
            placement="start"
          >
            <IconSettingsLine size="x-small" />
          </IconButton>

        }
        mountNode={mountNode}
        themeOverride={{
          minWidth: "250px"
        }}
      >
        {(checkPermissionsOr([
          {
            action: 'sp:ModifyExamEvent',
            resource: AppStore.getIamResourceString(`session/${ExamSessionStore.sessionSid}`)
          },
          {
            action: 'sp:CreateExamEvent',
            resource: AppStore.getIamResourceString(`session/${ExamSessionStore.sessionSid}`)
          }
          ,
          {
            action: 'sp:DescribeExamEvent',
            resource: AppStore.getIamResourceString(`session/${ExamSessionStore.sessionSid}`)
          }
        ])
        ) && (
            <Menu.Item onSelect={handleOpenIncidentReportModal}>
              <NavigationalMenuItem
                shortcut={CONSTANTS?.KEYBOARD_SHORTCUTS?.INCIDENT_REPORT?.OPTIONS?.CREATE?.KEY} >
                <Text variant="contentSmall">{incidentReport ? 'View' : 'Add'} Incident Report</Text>
              </NavigationalMenuItem>

            </Menu.Item>
          )}

        {(reviewRequired && !reviewCompletedDate && checkPermission('sp:DeleteExamAnnotation', AppStore.getIamResourceString(`session/${ExamSessionStore.sessionSid}`))) && (
          <Menu.Item onSelect={handleSetAsAudited}>
            <Text variant="contentSmall">Set as Audited</Text>
          </Menu.Item>
        )}


        <Menu.Item onSelect={() => setIsSessionLogModalOpen(true)}>
          <NavigationalMenuItem
            shortcut={CONSTANTS?.KEYBOARD_SHORTCUTS?.VIEW?.OPTIONS?.SESSION_LOG?.KEY} >
            <Text variant="contentSmall">Session Log</Text>
          </NavigationalMenuItem>
        </Menu.Item>

        <Menu.Separator />

        <Menu.Item onSelect={handleOpenShortcutsModal}>
          <NavigationalMenuItem
            shortcut={CONSTANTS?.KEYBOARD_SHORTCUTS?.VIEW?.OPTIONS?.HELP?.KEY} >
            <Text variant="contentSmall">Keyboard Shortcuts</Text>
          </NavigationalMenuItem>
        </Menu.Item>

      </Menu>

      <DraggableModal
        isOpen={isShortcutsModalOpen}
        onClose={handleCloseShortcutsModal}
        title="Keyboard Shortcuts"
        initialSize={initialSize}
        resizable={true}
        minimizable={true}
        minSize={{ width: 400, height: 300 }}
        maxSize={{ width: 500, height: 1000 }}
        constrainToContainer={false}
        id="keyboard-shortcuts-modal"
        mountNode={mountNode}
        contentStyle={{
          padding: '0'
        }}
      >
        <Shortcuts />
      </DraggableModal>

      <DraggableModal
        isOpen={isIncidentReportModalOpen}
        onClose={handleCloseIncidentReportModal}
        title="Incident Report"
        initialSize={initialSize}
        resizable={true}
        minimizable={true}
        minSize={{ width: 400, height: 300 }}
        maxSize={{ width: 500, height: 1000 }}
        constrainToContainer={false}
        id="incident-report-modal"
        mountNode={mountNode}
        contentStyle={{
          padding: '0'
        }}
      >
        <IncidentReport onClose={handleCloseIncidentReportModal} />
      </DraggableModal>

      <DraggableModal
        isOpen={isSessionLogModalOpen}
        onClose={() => setIsSessionLogModalOpen(false)}
        title="Session Log"
        initialSize={{ width: '50vw', height: '50vh' }}
        initialPosition="bottom right"
        resizable={true}
        minimizable={true}
        minSize={{ width: 400, height: 300 }}
        maxSize={{ width: 1000, height: 2000 }}
        constrainToContainer={false}
        id="session-log-modal"
        mountNode={mountNode}
        contentStyle={{
          padding: '0'
        }}
      >
        <SessionLog />
      </DraggableModal>

      <Modal
        open={isDraftWarningModalOpen}
        onDismiss={handleCloseDraftWarningModal}
        size="small"
        label="Draft Incident Report Warning"
        mountNode={mountNode}
      >
        <Modal.Header>
          <Text size="large" weight="bold">
            Cannot Set As Audited
          </Text>
        </Modal.Header>
        <Modal.Body>
          <Text size="medium" display="block" margin="0 0 medium 0">
            There is a <Link onClick={() => {
              handleCloseDraftWarningModal();
              handleOpenIncidentReportModal();
            }}>draft incident report</Link> that must be resolved before marking this session as audited.
          </Text>
          <Text size="medium" display="block">
            Please either submit or delete the draft incident report, then try again.
          </Text>
        </Modal.Body>
        <Modal.Footer>
          <Flex direction="row" justifyItems="end" gap="small">
            <Flex.Item>
              <Button onClick={handleCloseDraftWarningModal}>
                OK
              </Button>
            </Flex.Item>
          </Flex>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SessionHelp;
